import type { InsertTakenSetups, Setup } from '@here/api'
import { useSetups, userId } from '@state'
import { createEffect, createResource, createSignal } from 'solid-js'
import { useService } from 'solid-services'
import { useChannelsReads } from '../../../../state/channelReads/state'
import {
	getAverageScore,
	getLeaderScore,
	getSetupPotentialHigQuality,
	handleGetSetup,
	handleGetSetupTaken,
	toggleSetupTaken,
} from './resource'

export function useSingle() {
	const { channelReadsResource } = useService(useChannelsReads)()
	const { setUpdateSetup, updateSetupResource, setDeleteSetup } =
		useService(useSetups)()
	const [setupId, setSetupId] = createSignal<string | undefined>()

	const ids = () => [setupId(), userId()]

	const [setupResource, { refetch }] = createResource(setupId, handleGetSetup)
	const [takenResource, { refetch: refetchTaken }] = createResource(ids, ([id, user]) =>
		handleGetSetupTaken(id, user),
	)
	const [takenAverageScoreResource] = createResource(setupId, getAverageScore)
	const [leaderScoreResource] = createResource(setupId, getLeaderScore)
	const [
		setupPotentialHigQualityResource,
		{ refetch: setupPotentialHigQualityResourceRefetch },
	] = createResource(setupId, getSetupPotentialHigQuality)

	const [upsertTakenSetup, setUpsertTakenSetup] = createSignal<InsertTakenSetups>()
	const [upsertTakenResource] = createResource(upsertTakenSetup, toggleSetupTaken)

	const subscribed = () =>
		channelReadsResource().find(
			(channelRead) => channelRead.channel_id === setupResource()?.channel_id,
		)?.is_subscribed

	const toggleTaken = async () => {
		const has_taken = Boolean(!takenResource()?.has_taken)
		const setup_id = setupId()
		const user_id = userId()

		setUpsertTakenSetup({
			setup_id,
			user_id,
			has_taken,
		})
	}

	const setScore = async (score: number) => {
		const has_taken = Boolean(takenResource()?.has_taken)
		const setup_id = setupId()
		const user_id = userId()

		setUpsertTakenSetup({
			setup_id,
			user_id,
			has_taken,
			score,
		})
	}

	createEffect(() => {
		if (upsertTakenResource.state === 'ready') {
			refetchTaken()
		}
	})

	createEffect(() => {
		if (updateSetupResource.state === 'ready') {
			refetch()
		}
	})

	const onArchiveSetup = () => {
		if (setupResource()) {
			const setup: Setup = setupResource()!
			setUpdateSetup({
				...setup,
				status: 'archived',
			})
		}
	}

	const onDeleteSetup = () => {
		if (setupResource()) {
			const setup: Setup = setupResource()!
			setDeleteSetup(setup.id)
		}
	}

	const isArchived = () => setupResource()?.status === 'archived'

	return {
		isArchived,
		onArchiveSetup,
		setSetupId,
		setScore,
		setupId,
		setupPotentialHigQualityResource,
		setupPotentialHigQualityResourceRefetch,
		setupResource,
		takenResource,
		toggleTaken,
		subscribed,
		onDeleteSetup,
		upsertTakenResource,
		takenAverageScoreResource,
		leaderScoreResource,
	}
}
